import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Check, X, Clock, Plus, Search, User, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ConsentRecord {
  id: string;
  student_user_id: string;
  consent_type: string;
  granted_by: string;
  granted_at: string;
  revoked_at: string | null;
}

interface StudentConsent {
  user_id: string;
  display_name: string | null;
  email: string;
  consents: ConsentRecord[];
}

interface ConsentManagerProps {
  schoolId: string;
}

const CONSENT_TYPES = [
  { id: 'assessment_data_sharing', label: 'Assessment Data Sharing', description: 'Allow school staff to view student assessment results' },
  { id: 'directory_information', label: 'Directory Information', description: 'Share basic student info (name, grade) with school staff' },
  { id: 'counselor_access', label: 'Counselor Access', description: 'Allow counselors to view detailed career path recommendations' },
];

const GRANTED_BY_OPTIONS = [
  { id: 'parent', label: 'Parent/Guardian' },
  { id: 'student_18plus', label: 'Student (18+)' },
  { id: 'guardian', label: 'Legal Guardian' },
];

const ConsentManager = ({ schoolId }: ConsentManagerProps) => {
  const [students, setStudents] = useState<StudentConsent[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [newConsent, setNewConsent] = useState({ consent_type: 'assessment_data_sharing', granted_by: 'parent' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchStudents = async () => {
    setLoading(true);
    // Get students in this school
    const { data: profiles } = await supabase
      .from('profiles' as never)
      .select('user_id, display_name, email')
      .eq('school_id', schoolId)
      .order('display_name');

    if (!profiles) { setLoading(false); return; }

    // Get consent records for this school
    const { data: consents } = await supabase
      .from('consent_records' as never)
      .select('*')
      .eq('school_id', schoolId);

    const consentMap = new Map<string, ConsentRecord[]>();
    (consents || []).forEach((c: ConsentRecord) => {
      const existing = consentMap.get(c.student_user_id) || [];
      existing.push(c);
      consentMap.set(c.student_user_id, existing);
    });

    const merged: StudentConsent[] = (profiles as { user_id: string; display_name: string | null; email: string }[]).map(p => ({
      ...p,
      consents: consentMap.get(p.user_id) || [],
    }));

    setStudents(merged);
    setLoading(false);
  };

  useEffect(() => { fetchStudents(); }, [schoolId]); // eslint-disable-line

  const filteredStudents = debouncedSearch
    ? students.filter(s =>
        (s.display_name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        s.email.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : students;

  const addConsent = async (studentUserId: string) => {
    setSaving(true);
    await supabase.from('consent_records' as never).insert({
      student_user_id: studentUserId,
      school_id: schoolId,
      consent_type: newConsent.consent_type,
      granted_by: newConsent.granted_by,
    } as never);
    setSaving(false);
    setAddingFor(null);
    setNewConsent({ consent_type: 'assessment_data_sharing', granted_by: 'parent' });
    fetchStudents();
  };

  const revokeConsent = async (consentId: string) => {
    await supabase.from('consent_records' as never).update({ revoked_at: new Date().toISOString() } as never).eq('id', consentId);
    fetchStudents();
  };

  const totalStudents = students.length;
  const studentsWithConsent = students.filter(s => s.consents.some(c => !c.revoked_at)).length;
  const consentRate = totalStudents > 0 ? Math.round((studentsWithConsent / totalStudents) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass-card p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Total Students</p>
          <p className="text-xl font-display font-bold text-foreground">{totalStudents}</p>
        </div>
        <div className="glass-card p-4 space-y-1">
          <p className="text-xs text-muted-foreground">With Consent</p>
          <p className="text-xl font-display font-bold text-primary">{studentsWithConsent}</p>
        </div>
        <div className="glass-card p-4 space-y-1">
          <p className="text-xs text-muted-foreground">Consent Rate</p>
          <p className={`text-xl font-display font-bold ${consentRate >= 80 ? 'text-accent' : consentRate >= 50 ? 'text-primary' : 'text-destructive'}`}>
            {consentRate}%
          </p>
        </div>
      </div>

      {/* FERPA Notice */}
      <div className="glass-card p-4 border-primary/20 bg-primary/5 flex items-start gap-3">
        <Shield className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/80 space-y-1">
          <p className="font-semibold">FERPA Compliance</p>
          <p>Under FERPA, schools must obtain written consent before disclosing personally identifiable information from student education records. Track consent status for each student below.</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search students..."
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Student List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      ) : filteredStudents.length === 0 ? (
        <div className="glass-card p-8 text-center text-muted-foreground">
          <p>No students found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredStudents.map((student) => {
            const activeConsents = student.consents.filter(c => !c.revoked_at);
            const hasFullConsent = CONSENT_TYPES.every(ct =>
              activeConsents.some(c => c.consent_type === ct.id)
            );
            const isAdding = addingFor === student.user_id;

            return (
              <motion.div
                key={student.user_id}
                layout
                className="glass-card p-4 space-y-3"
              >
                {/* Student header */}
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    hasFullConsent ? 'bg-accent/10' : activeConsents.length > 0 ? 'bg-primary/10' : 'bg-secondary'
                  }`}>
                    {hasFullConsent ? (
                      <Check className="w-4 h-4 text-accent" />
                    ) : activeConsents.length > 0 ? (
                      <Clock className="w-4 h-4 text-primary" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {student.display_name || student.email.split('@')[0]}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                      hasFullConsent ? 'bg-accent/10 text-accent' :
                      activeConsents.length > 0 ? 'bg-primary/10 text-primary' :
                      'bg-secondary text-muted-foreground'
                    }`}>
                      {hasFullConsent ? 'Full Consent' : activeConsents.length > 0 ? 'Partial' : 'No Consent'}
                    </span>
                    <button
                      onClick={() => setAddingFor(isAdding ? null : student.user_id)}
                      className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
                    >
                      {isAdding ? <X className="w-4 h-4 text-muted-foreground" /> : <Plus className="w-4 h-4 text-primary" />}
                    </button>
                  </div>
                </div>

                {/* Active consents */}
                {activeConsents.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {activeConsents.map((consent) => {
                      const typeInfo = CONSENT_TYPES.find(ct => ct.id === consent.consent_type);
                      return (
                        <div key={consent.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-accent/5 border border-accent/10">
                          <Check className="w-3 h-3 text-accent" />
                          <span className="text-[10px] text-foreground">{typeInfo?.label || consent.consent_type}</span>
                          <span className="text-[9px] text-muted-foreground">({consent.granted_by})</span>
                          <button
                            onClick={() => revokeConsent(consent.id)}
                            className="ml-1 p-0.5 rounded hover:bg-destructive/10 transition-colors"
                            title="Revoke consent"
                          >
                            <X className="w-2.5 h-2.5 text-destructive/60" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add consent form */}
                <AnimatePresence>
                  {isAdding && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-2 border-t border-border/30 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Consent Type</label>
                            <select
                              value={newConsent.consent_type}
                              onChange={(e) => setNewConsent(prev => ({ ...prev, consent_type: e.target.value }))}
                              className="w-full mt-1 px-3 py-2 rounded-lg bg-card border border-border text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              {CONSENT_TYPES.map(ct => (
                                <option key={ct.id} value={ct.id}>{ct.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Granted By</label>
                            <select
                              value={newConsent.granted_by}
                              onChange={(e) => setNewConsent(prev => ({ ...prev, granted_by: e.target.value }))}
                              className="w-full mt-1 px-3 py-2 rounded-lg bg-card border border-border text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                              {GRANTED_BY_OPTIONS.map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <button
                          onClick={() => addConsent(student.user_id)}
                          disabled={saving}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:brightness-110 transition-all disabled:opacity-50"
                        >
                          {saving ? (
                            <div className="w-3 h-3 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
                          ) : (
                            <Shield className="w-3 h-3" />
                          )}
                          Record Consent
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConsentManager;